import { useCallback, useRef, useState, type MutableRefObject } from "react";
import {
  getAgentServerErrorMessage,
  isTokenExpiredServerError,
  parseAgentServerErrorPayload
} from "@/shared/agent-server-error";
import type { DebugTraceEvent } from "@/shared/types/debug-trace";

type ClientMetricName =
  | "send_started"
  | "send_succeeded"
  | "send_failed"
  | "auth_retry_started"
  | "auth_retry_succeeded"
  | "auth_retry_failed";

interface UseChatSendWithAuthRetryOptions<TArgs extends unknown[]> {
  sendMessage: (...args: TArgs) => Promise<void>;
  resolveAuthorizationToken: (forceRefresh: boolean) => Promise<string | null>;
  emitClientDebugEvent: (event: Partial<DebugTraceEvent>) => void;
  retryAccessTokenRef: MutableRefObject<string | null>;
}

export interface ChatSendWithAuthRetryResult<TArgs extends unknown[]> {
  sendMessageWithAuthRetry: (...args: TArgs) => Promise<void>;
  sendFailureMessage: string | null;
  authFailureMessage: string | null;
  clearSendFailureMessage: () => void;
}

export function useChatSendWithAuthRetry<TArgs extends unknown[]>({
  sendMessage,
  resolveAuthorizationToken,
  emitClientDebugEvent,
  retryAccessTokenRef
}: UseChatSendWithAuthRetryOptions<TArgs>): ChatSendWithAuthRetryResult<TArgs> {
  const [authFailureMessage, setAuthFailureMessage] = useState<string | null>(
    null
  );
  const [sendFailureMessage, setSendFailureMessage] = useState<string | null>(
    null
  );

  const metricCountsRef = useRef<Record<ClientMetricName, number>>({
    send_started: 0,
    send_succeeded: 0,
    send_failed: 0,
    auth_retry_started: 0,
    auth_retry_succeeded: 0,
    auth_retry_failed: 0
  });

  const recordClientMetric = useCallback(
    (
      metric: ClientMetricName,
      options?: {
        category?: "auth" | "chat";
        level?: "info" | "warn" | "error";
        payload?: Record<string, unknown>;
      }
    ) => {
      const nextCount = (metricCountsRef.current[metric] ?? 0) + 1;
      metricCountsRef.current[metric] = nextCount;
      if (typeof window !== "undefined") {
        (
          window as Window & {
            __claimMosaicAgentMetrics?: Record<ClientMetricName, number>;
          }
        ).__claimMosaicAgentMetrics = { ...metricCountsRef.current };
      }

      emitClientDebugEvent({
        category: options?.category ?? "chat",
        level: options?.level ?? "info",
        title: `metric:${metric}`,
        payload: {
          count: nextCount,
          ...(options?.payload ?? {})
        }
      });
    },
    [emitClientDebugEvent]
  );

  const sendMessageWithAuthRetry = useCallback(
    async (...args: TArgs) => {
      recordClientMetric("send_started");
      setSendFailureMessage(null);
      setAuthFailureMessage(null);

      try {
        await sendMessage(...args);
        recordClientMetric("send_succeeded");
      } catch (error) {
        const authError = parseAgentServerErrorPayload(error);
        if (
          authError?.status === 401 &&
          authError.code !== "AUTH_TOKEN_EXPIRED"
        ) {
          emitClientDebugEvent({
            category: "auth",
            level: "error",
            title: "Authentication failed without retry",
            payload: {
              code: authError.code,
              status: authError.status
            }
          });
          setAuthFailureMessage(getAgentServerErrorMessage(error));
          recordClientMetric("send_failed", {
            category: "auth",
            level: "error",
            payload: {
              code: authError.code,
              status: authError.status
            }
          });
        }

        if (!isTokenExpiredServerError(error)) {
          if (!authError) {
            setSendFailureMessage(getAgentServerErrorMessage(error));
            recordClientMetric("send_failed", {
              level: "error",
              payload: {
                reason: "non_auth_error"
              }
            });
          }
          throw error;
        }

        recordClientMetric("auth_retry_started", {
          category: "auth",
          level: "warn"
        });
        emitClientDebugEvent({
          category: "auth",
          level: "warn",
          title: "Token expired; retry started"
        });

        const refreshedToken = await resolveAuthorizationToken(true);
        if (!refreshedToken) {
          recordClientMetric("auth_retry_failed", {
            category: "auth",
            level: "error",
            payload: {
              reason: "refresh_failed"
            }
          });
          recordClientMetric("send_failed", {
            category: "auth",
            level: "error",
            payload: {
              reason: "refresh_failed"
            }
          });
          emitClientDebugEvent({
            category: "auth",
            level: "error",
            title: "Token refresh failed before retry"
          });
          setAuthFailureMessage("Session refresh failed. Please sign in again.");
          throw error;
        }

        try {
          retryAccessTokenRef.current = refreshedToken;
          await sendMessage(...args);
          setAuthFailureMessage(null);
          setSendFailureMessage(null);
          recordClientMetric("auth_retry_succeeded", {
            category: "auth",
            payload: {
              status: 200
            }
          });
          recordClientMetric("send_succeeded");
          emitClientDebugEvent({
            category: "auth",
            level: "info",
            title: "Retry succeeded with refreshed token"
          });
        } catch (retryError) {
          const retryPayload = parseAgentServerErrorPayload(retryError);
          recordClientMetric("auth_retry_failed", {
            category: "auth",
            level: "error",
            payload: {
              code: retryPayload?.code,
              status: retryPayload?.status
            }
          });
          recordClientMetric("send_failed", {
            category: "auth",
            level: "error",
            payload: {
              code: retryPayload?.code,
              status: retryPayload?.status
            }
          });
          emitClientDebugEvent({
            category: "auth",
            level: "error",
            title: "Retry failed after token refresh",
            payload: {
              code: retryPayload?.code,
              status: retryPayload?.status
            }
          });
          setAuthFailureMessage(getAgentServerErrorMessage(retryError));
          throw retryError;
        } finally {
          retryAccessTokenRef.current = null;
        }
      }
    },
    [
      emitClientDebugEvent,
      recordClientMetric,
      resolveAuthorizationToken,
      retryAccessTokenRef,
      sendMessage
    ]
  );

  const clearSendFailureMessage = useCallback(() => {
    setSendFailureMessage(null);
  }, []);

  return {
    sendMessageWithAuthRetry,
    sendFailureMessage,
    authFailureMessage,
    clearSendFailureMessage
  };
}
